/**
 * Retry/timeout decision logic for the `/api/db` DatabasesProxy fetch layer
 * (`lib/appwrite.ts`).
 *
 * Kept as a pure, dependency-free module so the decision logic is unit-testable
 * (`tests/unit/lib/proxy-retry.test.ts`) — `lib/appwrite.ts` pulls in SDK
 * clients and is excluded from vitest coverage.
 *
 * Policy (conservative by design):
 * - Every proxy fetch gets a hard timeout (`PROXY_REQUEST_TIMEOUT_MS`) so a
 *   hung request on flaky tablet wifi fails instead of spinning forever.
 * - ONLY idempotent read actions (`list`, `get`) are retried automatically,
 *   on transient failures (network error, timeout, 502/503/504), with a short
 *   exponential backoff and at most `MAX_READ_RETRIES` retries.
 * - Writes (`create`, `update`, `delete`, `transaction`, `batch_create`,
 *   `increment`, `decrement`, `next_sequence`, `upsert`, `bulk_*`) are NEVER
 *   retried here: a timed-out write may have committed server-side, and a
 *   blind resend double-submits on money paths (charges, tickets, sequences).
 * - This retry is separate from the server's runaway-guardrail `retryMs`
 *   signal (a 200 response), which `DatabasesProxy.request` handles on its
 *   own — the two never apply to the same response.
 */

/** Abort a proxy fetch that has produced no response after this long. */
export const PROXY_REQUEST_TIMEOUT_MS = 15_000

/** Max automatic retries for idempotent reads (the initial attempt not counted). */
export const MAX_READ_RETRIES = 2

/** Base backoff delay; doubles per retry (300ms, then 600ms). */
export const RETRY_BASE_DELAY_MS = 300

/** Proxy actions safe to resend blindly — reads with no server-side effect. */
const IDEMPOTENT_READ_ACTIONS: ReadonlySet<string> = new Set(["list", "get"])

/** Gateway statuses treated as transient (LB/edge hiccup, brief backend restart). */
const RETRYABLE_HTTP_STATUSES: ReadonlySet<number> = new Set([502, 503, 504])

/** Classified outcome of one failed proxy fetch attempt. */
export type ProxyFailure =
  | { kind: "network" } // fetch rejected: offline, DNS, connection reset
  | { kind: "timeout" } // our AbortController timeout fired
  | { kind: "http"; status: number } // response received with a non-2xx status

/** Whether this failure class is transient enough to be worth resending. */
export function isRetryableFailure(failure: ProxyFailure): boolean {
  if (failure.kind === "network" || failure.kind === "timeout") return true
  return RETRYABLE_HTTP_STATUSES.has(failure.status)
}

/**
 * Decide whether a failed proxy request should be automatically retried.
 *
 * @param action  `action` field from the request body (unknown-typed on purpose —
 *                callers pass `body.action` straight from a loose record)
 * @param failure classified failure of the attempt that just ran
 * @param attempt zero-based index of the attempt that just failed (0 = first try)
 */
export function shouldRetry(action: unknown, failure: ProxyFailure, attempt: number): boolean {
  if (typeof action !== "string" || !IDEMPOTENT_READ_ACTIONS.has(action)) return false
  if (attempt >= MAX_READ_RETRIES) return false
  return isRetryableFailure(failure)
}

/** Backoff before retrying after zero-based `attempt`: 300ms, 600ms, ... */
export function retryDelayMs(attempt: number): number {
  return RETRY_BASE_DELAY_MS * 2 ** Math.max(0, attempt)
}

/**
 * Classify a `fetch()` rejection. Our AbortController timeout surfaces as a
 * DOMException named "AbortError" ("TimeoutError" covers `AbortSignal.timeout`
 * should the call site ever switch); anything else is a plain network failure.
 */
export function classifyFetchError(err: unknown): ProxyFailure {
  if (err && typeof err === "object") {
    const name = (err as { name?: unknown }).name
    if (name === "AbortError" || name === "TimeoutError") return { kind: "timeout" }
  }
  return { kind: "network" }
}
